/* eslint-disable react/display-name */
import { yupResolver } from '@hookform/resolvers/yup'
import CloseRounded from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Location } from 'api/models/Common/Location'
import AddressSearch from 'components/Common/AddressSearch/AddressSearch'
import DirectionsMap from 'components/Common/DirectionsMap/DirectionsMap'
import ErrorMessage from 'components/Common/ErrorMessage/ErrorMessage'
import FormInput from 'components/FormFields/FormInput/FormInput'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Supplier } from '../../../../api/supplier/suppliers.types'
import { suppliersAPI } from '../../../../api/supplier/suppliersAPI'
import { FixedRouteSchema } from '../../../../app/validationSchemas/Supplier/FixedRouteSchema'
import ConfirmDialog from '../../../ConfirmDialog/ConfirmDialog'
import Notification from '../../../Notification/Notification'
import styles from './fixedRoutesStyle'
import FixedRoutesTable from './FixedRoutesTable'

export interface NotificationType {
  isOpen: boolean
  message: string
  type: string
}

const FixedRoutes = () => {
  const supplier: Supplier = useSelector(
    (state: any) => state.suppliers.supplier,
  )
  const [openForm, setOpenForm] = React.useState(false)
  const [notify, setNotify] = React.useState<NotificationType>({
    isOpen: false,
    message: '',
    type: '',
  })
  const [confirmDialog, setConfirmDialog] = React.useState({
    isOpen: false,
    title: '',
    subtitle: '',
    onConfirm: '' as any,
  })
  const [mapPickup, setMapPickup] = React.useState<Location | undefined>()
  const [mapDropoff, setMapDropoff] = React.useState<Location | undefined>()
  const [partialPickup, setPartialPickup] = React.useState(false)
  const [partialDropoff, setPartialDropoff] = React.useState(false)
  const [selectedRoute, setSelectedRoute] = React.useState<any>(null)
  const [addressSearchVersion, setAddressSearchVersion] = React.useState(0)

  const updateRouteInfo = (routeInfo: any) => {
    console.log(routeInfo)
  }

  const supplierFixedRoutes = useQuery(
    ['getSupplierFixedRoutes', supplier.id],
    async () => suppliersAPI.getSupplierFixedRoutes(supplier.id),
  )

  const { mutateAsync: createRoute } = useMutation((payload: any) =>
    suppliersAPI.createSupplierFixedRoute(
      payload.supplierId,
      payload.fixedRoute,
    ),
  )

  const formMethods = useForm({
    resolver: yupResolver(FixedRouteSchema),
  })

  const resetDrawerState = () => {
    formMethods.reset({
      name: '',
      pickup: undefined,
      dropOff: undefined,
      rates: [{ vehicleTypeName: '', price: '' }],
    })
    setMapPickup(undefined)
    setMapDropoff(undefined)
    setPartialPickup(false)
    setPartialDropoff(false)
    setSelectedRoute(null)
    setAddressSearchVersion((current) => current + 1)
  }

  const closeForm = () => {
    setOpenForm(false)
    resetDrawerState()
  }

  const onSubmit = async (data: FormData, event: any) => {
    event.preventDefault()
    await createRoute({
      fixedRoute: data,
      supplierId: supplier.id,
    })
    await supplierFixedRoutes.refetch()
    closeForm()
    setNotify({
      isOpen: true,
      message: 'Route Created',
      type: 'success',
    })
  }

  const handleSelectFrom = (value: any) => {
    formMethods.setValue('pickup', value)
    setMapPickup(value)
  }

  const handleSelectTo = (value: any) => {
    formMethods.setValue('dropOff', value)
    setMapDropoff(value)
  }

  const handleTogglePartialPickup = () => {
    setPartialPickup((prev) => !prev)
    formMethods.setValue('pickup', undefined)
    setMapPickup(undefined)
    setAddressSearchVersion((current) => current + 1)
  }

  const handleTogglePartialDropoff = () => {
    setPartialDropoff((prev) => !prev)
    formMethods.setValue('dropOff', undefined)
    setMapDropoff(undefined)
    setAddressSearchVersion((current) => current + 1)
  }

  const handlePartialPickupChange = (value: string) => {
    const upper = value.toUpperCase()
    formMethods.setValue('pickup', {
      postCode: upper,
      addressLine1: upper,
      formattedAddress: upper,
      isPartial: true,
    } as any)
  }

  const handlePartialDropoffChange = (value: string) => {
    const upper = value.toUpperCase()
    formMethods.setValue('dropOff', {
      postCode: upper,
      addressLine1: upper,
      formattedAddress: upper,
      isPartial: true,
    } as any)
  }

  const handleNewFixedRoute = () => {
    resetDrawerState()
    setOpenForm(true)
  }

  const openExistingRoute = React.useCallback(
    (route: any) => {
      const defaultRate = route?.rates?.[0]

      setSelectedRoute(route)
      setMapPickup(route?.pickup?.isPartial ? undefined : route?.pickup)
      setMapDropoff(route?.dropOff?.isPartial ? undefined : route?.dropOff)
      setPartialPickup(Boolean(route?.pickup?.isPartial))
      setPartialDropoff(Boolean(route?.dropOff?.isPartial))
      formMethods.reset({
        name: route?.name || '',
        pickup: route?.pickup,
        dropOff: route?.dropOff,
        rates: [
          {
            vehicleTypeName: defaultRate?.vehicleTypeName || '',
            price: defaultRate?.price ?? '',
          },
        ],
      })
      setAddressSearchVersion((current) => current + 1)
      setOpenForm(true)
    },
    [formMethods],
  )

  const previewPickup = formMethods.watch('pickup')
  const previewDropoff = formMethods.watch('dropOff')
  const isEditMode = Boolean(selectedRoute?.id)
  const pickupAddressValue = previewPickup
    ? {
        value: previewPickup,
        label:
          previewPickup.formattedAddress ||
          [
            previewPickup.addressLine1,
            previewPickup.town,
            previewPickup.postCode,
          ]
            .filter(Boolean)
            .join(', '),
      }
    : null
  const dropoffAddressValue = previewDropoff
    ? {
        value: previewDropoff,
        label:
          previewDropoff.formattedAddress ||
          [
            previewDropoff.addressLine1,
            previewDropoff.town,
            previewDropoff.postCode,
          ]
            .filter(Boolean)
            .join(', '),
      }
    : null

  return (
    <Box component={'div'}>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={styles.buttonGrid}>
          <Button
            variant="outlined"
            className={`${styles.round} ${styles['tumblr']}`}
            onClick={() => {
              handleNewFixedRoute()
            }}
          >
            Add Fixed Route +
          </Button>
        </Grid>
        <Grid item xs={12} sx={styles.componentPanel}>
          <FixedRoutesTable
            supplierFixedRoutes={supplierFixedRoutes}
            setNotify={setNotify}
            setConfirmDialog={setConfirmDialog}
            setFixedRoute={(e) => {
              openExistingRoute(e)
            }}
          />
        </Grid>
        <Drawer
          anchor={'right'}
          open={openForm}
          onClose={closeForm}
          PaperProps={{ sx: styles.drawerPaper }}
        >
          <FormProvider {...formMethods}>
            <Box
              component="form"
              onSubmit={formMethods.handleSubmit(onSubmit)}
              sx={styles.drawerForm}
            >
              <Box sx={styles.drawerHeader}>
                <Box>
                  <Typography sx={styles.drawerEyebrow}>
                    Fixed Routes
                  </Typography>
                  <Typography variant="h5" sx={styles.drawerTitle}>
                    {isEditMode
                      ? 'View Supplier Fixed Route'
                      : 'Create Supplier Fixed Route'}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="close fixed route panel"
                  onClick={closeForm}
                  sx={styles.closeButton}
                >
                  <CloseRounded />
                </IconButton>
              </Box>
              <Box sx={styles.drawerBody}>
                <Box sx={styles.formSection}>
                  <Box sx={styles.sectionHeader}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>
                        Route setup
                      </Typography>
                      <Typography sx={styles.sectionDescription}>
                        Start with a clear name, then add the pickup and dropoff
                        you want to reuse.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <FormInput
                        name={'name'}
                        label="Route name"
                        placeholder="Airport transfer, office shuttle, hospital run..."
                        sx={styles.routeNameInput}
                        disabled={isEditMode}
                      />
                    </Grid>
                    {mapPickup && mapDropoff && (
                      <Grid item xs={12}>
                        <Box sx={styles.mapCard}>
                          <Typography sx={styles.mapTitle}>
                            Route preview
                          </Typography>
                          <DirectionsMap
                            origin={mapPickup}
                            destination={mapDropoff}
                            mapWidth="100%"
                            mapHeight="260px"
                            updateRouteInfo={updateRouteInfo}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
                <Box sx={styles.formSection}>
                  <Box sx={styles.sectionHeader}>
                    <Box>
                      <Typography sx={styles.sectionTitle}>Stops</Typography>
                      <Typography sx={styles.sectionDescription}>
                        Choose whether each stop needs an exact match or can be
                        treated as a broader area.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Box sx={styles.locationCard}>
                        <Box sx={styles.locationCardRow}>
                          <Box sx={styles.locationCardCopy}>
                            <Typography sx={styles.locationTitle}>
                              Pickup
                            </Typography>
                          </Box>
                          <Box sx={styles.locationSearchWrap}>
                            {partialPickup ? (
                              <TextField
                                fullWidth
                                placeholder="Partial postcode (e.g. CH3 5)"
                                defaultValue={
                                  previewPickup?.isPartial
                                    ? previewPickup.postCode
                                    : ''
                                }
                                onChange={(e) =>
                                  handlePartialPickupChange(e.target.value)
                                }
                                disabled={isEditMode}
                              />
                            ) : (
                              <AddressSearch
                                key={`pickup-${addressSearchVersion}`}
                                onAddresssSelected={handleSelectFrom}
                                addressPlaceholder={'Pickup'}
                                name={'pickup'}
                                styles={styles.addressSearchStyles}
                                value={pickupAddressValue}
                                disabled={isEditMode}
                                clientId={0}
                                requirePostcode
                              />
                            )}
                            <FormControlLabel
                              sx={styles.switchControl}
                              control={
                                <Switch
                                  checked={partialPickup}
                                  onChange={handleTogglePartialPickup}
                                  color="primary"
                                  disabled={isEditMode}
                                />
                              }
                              label={
                                <Typography sx={styles.switchLabel}>
                                  {partialPickup
                                    ? 'Partial pickup'
                                    : 'Exact pickup'}
                                </Typography>
                              }
                              labelPlacement="start"
                            />
                            <ErrorMessage
                              setFromResult={false}
                              message={
                                formMethods.formState.errors?.pickup
                                  ?.addressLine1.message
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={styles.locationCard}>
                        <Box sx={styles.locationCardRow}>
                          <Box sx={styles.locationCardCopy}>
                            <Typography sx={styles.locationTitle}>
                              Dropoff
                            </Typography>
                          </Box>
                          <Box sx={styles.locationSearchWrap}>
                            {partialDropoff ? (
                              <TextField
                                fullWidth
                                placeholder="Partial postcode (e.g. CH3 5)"
                                defaultValue={
                                  previewDropoff?.isPartial
                                    ? previewDropoff.postCode
                                    : ''
                                }
                                onChange={(e) =>
                                  handlePartialDropoffChange(e.target.value)
                                }
                                disabled={isEditMode}
                              />
                            ) : (
                              <AddressSearch
                                key={`dropoff-${addressSearchVersion}`}
                                onAddresssSelected={handleSelectTo}
                                addressPlaceholder={'Drop Off'}
                                name={'dropOff'}
                                styles={styles.addressSearchStyles}
                                value={dropoffAddressValue}
                                disabled={isEditMode}
                                clientId={0}
                                requirePostcode
                              />
                            )}
                            <FormControlLabel
                              sx={styles.switchControl}
                              control={
                                <Switch
                                  checked={partialDropoff}
                                  onChange={handleTogglePartialDropoff}
                                  color="primary"
                                  disabled={isEditMode}
                                />
                              }
                              label={
                                <Typography sx={styles.switchLabel}>
                                  {partialDropoff
                                    ? 'Partial dropoff'
                                    : 'Exact dropoff'}
                                </Typography>
                              }
                              labelPlacement="start"
                            />
                            <ErrorMessage
                              setFromResult={false}
                              message={
                                formMethods.formState.errors?.dropOff
                                  ?.addressLine1.message
                              }
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              {!isEditMode && (
                <Box sx={styles.drawerFooter}>
                  <Box sx={styles.footerActionGroup}>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={styles.primaryAction}
                    >
                      Save route
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </FormProvider>
        </Drawer>
        <Notification notify={notify} setNotify={setNotify} />
        <ConfirmDialog
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
        />
      </Grid>
    </Box>
  )
}

export default FixedRoutes
